<script setup>
import { defineProps, computed, onUnmounted, onMounted, ref } from 'vue';
import useVariant from './useVariant';
import VariantSelector from './VariantSelector.vue';
import { formatMoney } from "@shopify/theme-currency";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Keyboard, Mousewheel, Navigation, Scrollbar } from 'swiper';

const { setVariantId, variantMenuOpen, setMenu } = useVariant();

const props = defineProps(['recommendations', 'heading']); 

const menuOpen = ref(false);
const product = ref();
const productId = ref();
const moneyFormat = ref();
const selectedOptions = ref();
const alternateColours = ref([]);

const recommendations = computed(() => {
  return props.recommendations;
});

const heading = computed(() => {
  return props.heading;
});

const handleVariantChange = (id, selectedProduct, options, altColours = []) => {
  setVariantId(id);
  setMenu(true);
  console.log(`Options: ${JSON.stringify(options)}`);
  product.value = selectedProduct;
  productId.value = selectedProduct.id;
  selectedOptions.value = options;
  alternateColours.value = altColours;
};

const updateProduct = (updatedProduct, updatedOptions, updatedAltColours) => {
  product.value = updatedProduct;
  selectedOptions.value = updatedOptions;
  alternateColours.value = updatedAltColours;
};

const closeMenu = () => {
  setMenu(false);
};

onMounted(() => {
  moneyFormat.value = window.theme.moneyFormat;
});

const calculatePrice = (amount) => {
  let price = amount * 100;
  return formatMoney(price, moneyFormat.value);
};
</script>

<template>
  <Transition>
    <VariantSelector 
      v-if="variantMenuOpen" 
      @close-menu="setMenu(false)" 
      @update-product="updateProduct"
      :product="product" 
      :id="productId"
      :options="selectedOptions"
      :alternate-colours="alternateColours"
     />
  </Transition>

  <div v-if="variantMenuOpen" class="app-overlay" @click="closeMenu"></div>
  <h2 class="product-complements__title">{{ heading }}</h2>
  <div class="swiper-button swiper-button-prev"></div>
  <div class="swiper-button swiper-button-next"></div>
  <swiper 
    :modules="[Autoplay, Keyboard, Mousewheel, Navigation, Scrollbar]" 
    :autoplay="true"
    :breakpoints="{ 750: {slidesPerView: 3 }, 1024: { slidesPerView: 1 }}"
    :keyboard="{enabled: true, onlyInViewport: true}"
    :mousewheel="{enabled: true, sensitivity: 4, forceToAxis: true}"
    :rewind="true"
    :navigation="{enabled: true, prevEl: '.swiper-button-prev', nextEl: '.swiper-button-next'}"
    :scrollbar="{draggable: true, el: '.swiper-scrollbar' }" 
    :slides-per-view="1" 
    :space-between="5" 
    :threshold="20"
  >
    <swiper-slide v-for=" rec in recommendations" v-if="recommendations"  :key="rec.product.id">
      <div class="product-complements__inner">

        <a :href="`/products/${rec?.product.handle}`" class="product-complements__image">
          <img :src="rec?.image?.src" loading="lazy" :alt="rec?.product.handle">
        </a>

        <div class="product-complements__content">
          <a :href="`/products/${rec?.product.handle}`">{{rec?.product?.title}}</a>

          <div class="product-complements__pricing">
            <p class="product-complements__price">{{ calculatePrice(rec?.price?.amount) }}</p>
            <button type="button" @click="handleVariantChange(rec.id, rec.product, rec.selectedOptions, rec.alternateColours)"
              class="product-complements__button button button--primary button--rounded button--fill" :data-alt-colours="`${rec?.alternateColours}`">add</button>
          </div>
        </div>
      </div>
    </swiper-slide>
  </swiper>
  <div class="product-complements__scrollbar swiper-scrollbar">
    <div class="swiper-scrollbar-drag"></div>
  </div>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: transform 0.2s ease;
}

.v-enter-from {
  transform: translateX(100%);
}

.v-leave-to {
  transform: translateX(100%);
}

.app-overlay {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 50;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  background-color: hsla(0, 0%, 7%, .20);
}
</style>
